import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

/**
 * Enterprise FAQ — written for Compliance Managers, Quality Managers,
 * Operations Directors, Clinic Directors, Pharmacy Owners and regulated
 * manufacturers. Every answer removes a real purchasing objection.
 */

const faqs = [
  {
    q: "How does CanniComply demonstrate inspection readiness to a regulator on the day?",
    a: "Every SOP acknowledgement, training completion and certificate is written to an immutable, time-stamped ledger with a cryptographic hash chain. Inspectors receive a read-only evidence pack — filterable by staff member, policy version and date — showing exactly who accepted what, when, and from which device. There is nothing to prepare on the morning of an inspection.",
  },
  {
    q: "How long is evidence retained and can it be produced years after an employee leaves?",
    a: "All compliance evidence is retained for seven years by default, in line with MHRA, CQC and HSE expectations, with optional extended retention for GxP environments. Records survive staff departures, role changes and policy revisions. Historical certificates remain independently verifiable at their original /verify URL indefinitely.",
  },
  {
    q: "How does the platform handle policy version control and superseded documents?",
    a: "Each SOP carries an immutable version number, effective date and superseded date. When a policy changes, prior versions remain readable in the audit ledger and every historical acknowledgement continues to reference the exact version the employee signed. Regulators can trace competency against the correct document — not the current one.",
  },
  {
    q: "How is staff competency verified rather than just recorded?",
    a: "Acknowledgements are combined with knowledge checks tied to each SOP, plus device and IP metadata at the point of signature. Managers see competency status per role, per site and per policy in real time, with automatic re-training triggers when a document is revised or an assessment is failed.",
  },
  {
    q: "How does CanniComply keep pace with changes to UK legislation and regulator guidance?",
    a: "Our regulatory team monitors MHRA, Home Office, HSE, CQC, GPhC and equivalent European bodies. Legislative changes are reflected in the platform's SOP library within five working days of publication, with change notes, redlines and re-acknowledgement workflows issued to affected users automatically.",
  },
  {
    q: "Where is our data hosted and what are the security guarantees?",
    a: "Data is hosted in UK region infrastructure, encrypted in transit (TLS 1.3) and at rest (AES-256). Access is role-scoped and audit-logged. We operate under a documented ISMS aligned to ISO 27001 controls, with quarterly penetration testing and a signed DPA available on request. We do not train third-party AI models on customer data.",
  },
  {
    q: "What does onboarding look like for a multi-site organisation?",
    a: "A dedicated implementation lead handles tenant setup, SSO configuration, role mapping and migration of your existing SOP set. Typical go-live for a single site is under two weeks; multi-site rollouts (3–20 sites) complete within four to six weeks. You retain a named account director for the life of the contract.",
  },
  {
    q: "What support is included and how do we escalate a compliance-critical issue?",
    a: "Every paid plan includes UK business-hours support with a four working-hour first response SLA. Enterprise plans include 24/7 priority incident support, a named CSM and a direct line to our compliance leadership for regulator-facing incidents. We do not outsource support offshore.",
  },
];

function Item({ item, index, open, onToggle }) {
  const isOpen = open === index;
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="border-b border-white/[0.06] last:border-b-0"
    >
      <button
        type="button"
        onClick={() => onToggle(index)}
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${index}`}
        className="group w-full flex items-start gap-6 py-6 md:py-7 text-left transition-colors"
      >
        <span className="mt-1 text-[11px] font-mono text-[#D4A65A]/70 tabular-nums shrink-0 w-6">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 font-display text-[17px] md:text-[19px] tracking-[-0.015em] text-white leading-snug font-medium group-hover:text-[#F0DBB0] transition-colors">
          {item.q}
        </span>
        <span
          className={`mt-1 h-8 w-8 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
            isOpen
              ? "border-[#D4A65A]/50 bg-[#D4A65A]/10"
              : "border-white/[0.1] bg-white/[0.02] group-hover:border-[#D4A65A]/30"
          }`}
        >
          {isOpen ? (
            <Minus size={13} className="text-[#D4A65A]" />
          ) : (
            <Plus size={13} className="text-slate-400 group-hover:text-[#D4A65A] transition-colors" />
          )}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pl-12 md:pl-12 pr-14 pb-7 md:pb-8">
              <p
                data-testid={`faq-answer-${index}`}
                className="text-[14.5px] md:text-[15px] text-slate-400 leading-[1.75] max-w-[70ch]"
              >
                {item.a}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);

  function toggle(i) {
    setOpen(open === i ? -1 : i);
  }

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 border-t border-white/[0.04]"
    >
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-20" />

      <div className="relative mx-auto max-w-6xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left column — heading */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="eyebrow">Frequently asked</div>
              <motion.h2
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.6 }}
                className="mt-4 font-display text-[34px] md:text-[42px] tracking-[-0.035em] text-white leading-[1.03] font-semibold"
              >
                Answers for the people accountable to a regulator.
              </motion.h2>
              <p className="mt-5 text-slate-400 text-[14.5px] leading-relaxed max-w-md">
                The questions we hear from Heads of Compliance, Quality
                Managers and Operations Directors before their first
                demonstration.
              </p>
              <div className="mt-8 hidden lg:flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500">
                <span className="h-px w-8 bg-[#D4A65A]/40" />
                <span className="text-[#D4A65A]/80 font-semibold">
                  {faqs.length} answers
                </span>
              </div>
            </div>
          </div>

          {/* Right column — questions */}
          <div className="lg:col-span-8">
            <div className="rounded-xl border border-white/[0.06] bg-gradient-to-b from-[#0B1424]/60 to-[#050B18]/40 px-6 md:px-9">
              {faqs.map((item, i) => (
                <Item
                  key={item.q}
                  item={item}
                  index={i}
                  open={open}
                  onToggle={toggle}
                />
              ))}
            </div>

            <div className="mt-8 flex items-center gap-3 text-[12.5px] text-slate-500 pl-1">
              <span className="h-1 w-1 rounded-full bg-[#0F5F4A]" />
              Still have a question?{" "}
              <a
                href="#contact"
                data-testid="faq-contact-link"
                className="text-[#D4A65A] hover:text-[#DEB675] transition-colors font-medium"
              >
                Speak to the team →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
